(() => {
  const STORAGE_KEY = 'preferredDimensionUnitV6';

  const DIMENSION_INPUT_SELECTOR =
    'input[name="filter.p.m.custom.misure"]';

  const UNIT_BUTTON_SELECTOR =
    '[data-dimension-unit]';

  const ACTIVE_DIMENSION_SELECTOR =
    '.js-active-dimension-label[data-dimension-value]';

  /**
   * Recupera l’unità salvata.
   * Valore predefinito: centimetri.
   */
  function getSelectedUnit() {
    try {
      const savedUnit = localStorage.getItem(STORAGE_KEY);

      return savedUnit === 'in' ? 'in' : 'cm';
    } catch (error) {
      return 'cm';
    }
  }

  /**
   * Salva la preferenza dell’utente.
   */
  function saveSelectedUnit(unit) {
    try {
      localStorage.setItem(STORAGE_KEY, unit);
    } catch (error) {
      // Lo switch continua a funzionare anche senza localStorage.
    }
  }

  /**
   * Formatta i decimali in base alla lingua del sito.
   *
   * Italiano: 58,3
   * Inglese: 58.3
   */
  function formatNumber(value) {
    const language =
      document.documentElement.lang?.toLowerCase() || '';

    const locale = language.startsWith('it')
      ? 'it-IT'
      : 'en-US';

    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1
    }).format(value);
  }

  /**
   * Converte una misura da centimetri o millimetri
   * a pollici.
   */
  function convertToInches(rawValue) {
    if (!rawValue) {
      return rawValue;
    }

    const normalizedValue = rawValue
      .trim()
      .replace(/,/g, '.')
      .replace(/\bWIDHT\b/gi, 'WIDTH');

    /*
     * Non converte valori non numerici.
     */
    if (/ONE\s*SIZE/i.test(normalizedValue)) {
      return rawValue;
    }

    const containsMillimetres =
      /\bMM\b/i.test(normalizedValue);

    const containsCentimetres =
      /\bCM\b/i.test(normalizedValue);

    /*
     * Se non sono presenti CM o MM,
     * il valore resta invariato.
     */
    if (
      !containsMillimetres &&
      !containsCentimetres
    ) {
      return rawValue;
    }

    /*
     * Se il valore contiene esclusivamente MM,
     * usa il divisore dei millimetri.
     * Negli altri casi usa quello dei centimetri.
     */
    const divisor =
      containsMillimetres && !containsCentimetres
        ? 25.4
        : 2.54;

    const hasWidthPrefix =
      /\bWIDTH\b/i.test(normalizedValue);

    const cleanedValue = normalizedValue
      .replace(/\bWIDTH\s*:?\s*/gi, '')
      .replace(/\bCM\b/gi, '')
      .replace(/\bMM\b/gi, '')
      .trim();

    const dimensionParts = cleanedValue
      .split(/\s*X\s*/i)
      .map((part) => part.trim())
      .filter(Boolean);

    const numericParts = dimensionParts.map((part) =>
      Number.parseFloat(part)
    );

    if (
      numericParts.length === 0 ||
      numericParts.some((number) =>
        Number.isNaN(number)
      )
    ) {
      return rawValue;
    }

    const convertedValue = numericParts
      .map((number) =>
        formatNumber(number / divisor)
      )
      .join(' X ');

    return hasWidthPrefix
      ? `WIDTH: ${convertedValue} IN`
      : `${convertedValue} IN`;
  }

  /**
   * Aggiorna le misure presenti nelle tendine
   * desktop e mobile.
   */
  function updateFilterDimensionLabels(selectedUnit) {
    document
      .querySelectorAll(DIMENSION_INPUT_SELECTOR)
      .forEach((input) => {
        const label = input.closest('label');

        if (!label) {
          return;
        }

        const textContainer = label.querySelector(
          '.facet-checkbox__text[aria-hidden="true"]'
        );

        if (!textContainer) {
          return;
        }

        /*
         * Legge il valore tecnico originale
         * direttamente dall’input Shopify.
         */
        const rawValue = input.value;

        const displayedValue =
          selectedUnit === 'in'
            ? convertToInches(rawValue)
            : rawValue;

        /*
         * Nuova struttura HTML:
         * modifica soltanto il testo della misura.
         */
        const dedicatedTextElement =
          textContainer.querySelector(
            '.js-dimension-label-text'
          );

        if (dedicatedTextElement) {
          if (
            dedicatedTextElement.textContent.trim() !==
            displayedValue
          ) {
            dedicatedTextElement.textContent =
              displayedValue;
          }

          return;
        }

        /*
         * Compatibilità con la vecchia struttura:
         * conserva il conteggio prodotti.
         */
        const currentText =
          textContainer.textContent.trim();

        const countMatch = currentText.match(
          /\s*(\([\d.,]+\s*(?:products?|prodotti?)?\))$/i
        );

        const newText = countMatch
          ? `${displayedValue} ${countMatch[1]}`
          : displayedValue;

        if (currentText !== newText) {
          textContainer.textContent = newText;
        }
      });
  }

  /**
   * Aggiorna le pillole dei filtri attivi.
   *
   * Esempio:
   * Misure: 148 CM X 8.5 CM
   * Misure: 58,3 X 3,3 IN
   */
  function updateActiveDimensionLabels(selectedUnit) {
    document
      .querySelectorAll(ACTIVE_DIMENSION_SELECTOR)
      .forEach((element) => {
        const rawValue =
          element.dataset.dimensionValue;

        if (!rawValue) {
          return;
        }

        const displayedValue =
          selectedUnit === 'in'
            ? convertToInches(rawValue)
            : rawValue;

        if (
          element.textContent.trim() !==
          displayedValue
        ) {
          element.textContent = displayedValue;
        }
      });
  }

  /**
   * Aggiorna lo stato visivo dei pulsanti CM/IN.
   */
  function updateUnitButtons(selectedUnit) {
    document
      .querySelectorAll(UNIT_BUTTON_SELECTOR)
      .forEach((button) => {
        const isActive =
          button.dataset.dimensionUnit ===
          selectedUnit;

        button.classList.toggle(
          'is-active',
          isActive
        );

        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });
  }

  /**
   * Aggiornamento completo dell’interfaccia.
   */
  function updateDimensionInterface(selectedUnit) {
    updateFilterDimensionLabels(selectedUnit);
    updateActiveDimensionLabels(selectedUnit);
    updateUnitButtons(selectedUnit);
  }

  function initialiseDimensionSwitch() {
    updateDimensionInterface(
      getSelectedUnit()
    );
  }

  /**
   * Intercetta il clic prima che il menu mobile
   * dei filtri Shopify possa reagire.
   */
  function handleDimensionUnitClick(event) {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest(
      UNIT_BUTTON_SELECTOR
    );

    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const selectedUnit =
      button.dataset.dimensionUnit;

    if (
      selectedUnit !== 'cm' &&
      selectedUnit !== 'in'
    ) {
      return;
    }

    saveSelectedUnit(selectedUnit);
    updateDimensionInterface(selectedUnit);
  }

  /*
   * true = fase di cattura.
   * Il clic viene gestito prima degli script
   * del menu mobile del tema.
   */
  document.addEventListener(
    'click',
    handleDimensionUnitClick,
    true
  );

  /*
   * Inizializzazione della pagina.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseDimensionSwitch
    );
  } else {
    initialiseDimensionSwitch();
  }

  /*
   * Supporto per l’editor del tema Shopify.
   */
  document.addEventListener(
    'shopify:section:load',
    initialiseDimensionSwitch
  );

  /*
   * I filtri vengono aggiornati via AJAX.
   * Quando Shopify sostituisce il contenuto,
   * lo script riapplica l’unità selezionata.
   */
  let updateScheduled = false;

  const observer = new MutationObserver(
    (mutations) => {
      const containsRelevantElements =
        mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) => {
              if (
                node.nodeType !==
                Node.ELEMENT_NODE
              ) {
                return false;
              }

              const element = node;

              return (
                element.matches?.(
                  DIMENSION_INPUT_SELECTOR
                ) ||
                element.matches?.(
                  UNIT_BUTTON_SELECTOR
                ) ||
                element.matches?.(
                  ACTIVE_DIMENSION_SELECTOR
                ) ||
                element.querySelector?.(
                  DIMENSION_INPUT_SELECTOR
                ) ||
                element.querySelector?.(
                  UNIT_BUTTON_SELECTOR
                ) ||
                element.querySelector?.(
                  ACTIVE_DIMENSION_SELECTOR
                )
              );
            }
          )
        );

      if (
        !containsRelevantElements ||
        updateScheduled
      ) {
        return;
      }

      updateScheduled = true;

      requestAnimationFrame(() => {
        updateScheduled = false;
        initialiseDimensionSwitch();
      });
    }
  );

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();